import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { Button } from '@deepguide-ai/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog';
import { queryClient, trpc } from '@/lib/trpc';
import { vendorLogoPath } from '@/lib/vendor-logo';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle';

function ConfluenceConfigModal({ open, onOpenChange, integration, workspaceId, onComplete }: ConfigModalProps) {
  const [connecting, setConnecting] = useState(false);
  const integrationId = integration?.id ?? '';
  const status = useQuery({ ...trpc.integrations.getConfluenceConnectionStatus.queryOptions({ integrationId }), enabled: open && !!integrationId });
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='sm:max-w-xl'>
      <DialogHeader>
        <DialogTitle>Confluence evidence</DialogTitle>
        <DialogDescription>Collect site groups and their members for access reviews.</DialogDescription>
      </DialogHeader>
      <ScrollArea type='scroll' className='max-h-[65vh]' viewClassName='max-h-[65vh] [&>div]:!block'>
      <div className='space-y-3 px-6 pb-4 text-sm'>
        {integrationId && status.isLoading ? <p role='status'>Loading Confluence connection…</p> : null}
        {status.error ? <div className='space-y-2'>
          <p role='alert' className='text-destructive'>{status.error.message}</p>
          <Button variant='outline' disabled={status.isFetching} onClick={() => void status.refetch()}>Retry loading</Button>
        </div> : status.data ? <p>Connected site: {status.data.siteName ?? status.data.providerAccount}</p> : null}
        <p className='text-muted-foreground'>Use Scan on the Confluence card to collect evidence and view progress and results. Use Access reviews to review collected accounts. Connecting alone does not collect evidence.</p>
        <p className='text-muted-foreground'>Collection covers visible site groups and their direct members. Groupless users, page and space permissions, administrator privileges, MFA and SSO require separate evidence.</p>
        <p className='text-muted-foreground'>Screenata requests user and group read access and permission to renew the connection. Reconnect to approve directory access if you connected before this update.</p>
        {status.data?.lastError ? <p role='alert' className='text-destructive'>{status.data.lastError}</p> : null}
        <p className='text-muted-foreground'>Disconnect from the integration card to stop future collection. Collected evidence is retained. Revoke app access in Confluence separately.</p>
      </div>
      </ScrollArea>
      <DialogFooter>
        <Button disabled={connecting} onClick={async () => {
          setConnecting(true);
          try { await ConfluenceProvider.handleConnect({ workspaceId, onSuccess: onComplete, onFinish: () => setConnecting(false) }); }
          catch { setConnecting(false); }
        }}>{connecting ? 'Connecting…' : integrationId ? 'Reconnect Confluence' : 'Connect Confluence'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

export const ConfluenceProvider: IntegrationProvider = {
  id: 'Confluence', name: 'Confluence', description: 'Site group access inventory',
  Icon: () => <img src={vendorLogoPath('confluence')} alt='' className='size-6 object-contain' />,
  ConfigModal: ConfluenceConfigModal, postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    { id: 'access', name: 'Access inventory', description: 'Read visible site groups and their members', enabled: true },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.initConfluenceAuth.mutationOptions()).execute(undefined).catch(error => {
      toast.error(error instanceof Error ? error.message : 'Could not start Confluence authorization');
      onFinish?.('error');
      throw error;
    });
    openOAuthConnectPopup({ authUrl: result.authUrl, windowName: 'confluence-auth', provider: 'Confluence', onSuccess, onFinish,
      onError: message => toast.error(message), onPopupBlocked: () => toast.error('Allow popups to connect Confluence.'),
    });
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.completeConfluenceAuth.mutationOptions()).execute({ code, state });
    if ('requiresSiteSelection' in result && result.requiresSiteSelection) return result;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getConfluenceConnectionStatus.queryKey() }),
    ]);
    return result;
  },
  completePendingConnect: async ({ selectionToken, siteId }) => {
    if (!siteId) throw new Error('Choose a Confluence site before connecting.');
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.finalizeConfluenceSelection.mutationOptions()).execute({ selectionToken, siteId });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getConfluenceConnectionStatus.queryKey() }),
    ]);
    return result;
  },
  syncData: async () => { throw new Error('Use Scan on the Confluence integration card to collect access evidence.'); },
};
